import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { AuditFlag, FlagStatus } from "@/types";
import { updateFlagStatus } from "@/api/audit";
import { DISMISS_REASONS } from "@/api/discrepancies";
import { DismissModal } from "@/components/reconciliation/DismissModal";
import { useToasts } from "@/hooks/useToast";

interface Props {
  flag: AuditFlag;
  appointmentId: string;
  /** Synced from ezyVet: offer the side-by-side reconciliation workbench. */
  synced?: boolean;
}

export function FlagCard({ flag, appointmentId, synced = false }: Props) {
  const qc = useQueryClient();
  const push = useToasts((s) => s.push);
  const [dismissing, setDismissing] = useState(false);

  const mutation = useMutation({
    mutationFn: (status: FlagStatus) => updateFlagStatus(flag.id, status),
    onSuccess: (_data, status) => {
      push(`Marked ${status}`, "success");
      qc.invalidateQueries({ queryKey: ["audit", "report", appointmentId] });
    },
    onError: (err: { message?: string }) => {
      push(err.message ?? "Update failed", "error");
    },
  });

  const isDone = flag.status !== "pending";

  return (
    <div className={`flag ${flag.severity}`}>
      <div className="flag-header">
        <div>
          <strong>{flag.event_description}</strong>
          {flag.recommended_action && (
            <div className="muted" style={{ marginTop: 4 }}>
              {flag.recommended_action}
            </div>
          )}
        </div>
        <span className={`badge ${flag.severity}`}>{flag.severity}</span>
      </div>

      <div className="flag-meta">
        <div>
          Billed: <strong>{flag.billed ? "Yes" : "No"}</strong>
        </div>
        <div>
          Confidence: <strong>{(flag.confidence * 100).toFixed(0)}%</strong>
        </div>
        <div>
          Value: <strong>${flag.estimated_value_usd.toFixed(2)}</strong>
        </div>
        {flag.matched_invoice_item && (
          <div>
            Matched: <strong className="mono">{flag.matched_invoice_item}</strong>
          </div>
        )}
        {flag.matched_line_id && (
          <div>
            Invoice line: <strong className="mono">#{flag.matched_line_id}</strong>
          </div>
        )}
      </div>

      {!isDone && (
        <div className="flag-actions">
          <button
            className="btn btn-sm btn-primary"
            disabled={mutation.isPending}
            onClick={() => mutation.mutate("added")}
          >
            Add to invoice
          </button>
          <button
            className="btn btn-sm"
            disabled={mutation.isPending}
            onClick={() => mutation.mutate("reviewed")}
          >
            Mark reviewed
          </button>
          <button
            className="btn btn-sm btn-danger"
            disabled={mutation.isPending}
            onClick={() => setDismissing(true)}
          >
            Dismiss
          </button>
        </div>
      )}
      {isDone && (
        <div className="muted">
          Status: {flag.status}
          {flag.dismiss_reason && ` · ${DISMISS_REASONS[flag.dismiss_reason]}`}
          {flag.review_notes && ` · ${flag.review_notes}`}
        </div>
      )}
      <DismissModal
        discrepancy={flag}
        open={dismissing}
        onClose={() => setDismissing(false)}
        onDismissed={() => {
          push("Dismissed", "success");
          qc.invalidateQueries({ queryKey: ["audit", "report", appointmentId] });
          qc.invalidateQueries({ queryKey: ["discrepancies"] });
        }}
      />
      {synced && (
        <Link to={`/reconciliation/${flag.id}`} className="btn btn-sm btn-ghost" style={{ marginTop: 8 }}>
          Open in reconciliation workbench →
        </Link>
      )}
    </div>
  );
}
